import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Platform,
  Dimensions,
  Pressable,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BlurView } from 'expo-blur';
import * as Haptics from 'expo-haptics';
import { Ionicons } from '@expo/vector-icons';
import useTheme from '../../hooks/useTheme';
import { useNotifications } from '../../hooks/use-notifications';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface TabIconProps {
  route: string;
  isFocused: boolean;
  color: string;
  size: number;
}

// Consistent icon set using Ionicons SF Symbols style
const getIconName = (routeName: string, focused: boolean): keyof typeof Ionicons.glyphMap => {
  const icons: Record<string, { focused: keyof typeof Ionicons.glyphMap; unfocused: keyof typeof Ionicons.glyphMap }> = {
    Dashboard: { focused: 'grid', unfocused: 'grid-outline' },
    Purchases: { focused: 'document-text', unfocused: 'document-text-outline' },
    Vendors: { focused: 'business', unfocused: 'business-outline' },
    Inventory: { focused: 'cube', unfocused: 'cube-outline' },
  };

  return icons[routeName]?.[focused ? 'focused' : 'unfocused'] || 'help-circle-outline';
};

export const AppleTabBar: React.FC<BottomTabBarProps> = ({
  state,
  descriptors,
  navigation,
}) => {
  const { colors, isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const { unreadCount } = useNotifications();
  
  // Get the active route's descriptor to check for tabBarStyle
  const activeDescriptor = descriptors[state.routes[state.index].key];
  const tabBarStyle = activeDescriptor?.options?.tabBarStyle as any;
  const isHidden = tabBarStyle?.display === 'none';
  
  // Animation refs for each tab
  const animations = useRef(
    state.routes.map(() => ({
      scale: new Animated.Value(1),
      opacity: new Animated.Value(0.6),
    }))
  ).current;

  // Pulse animation for center button
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Animate tabs based on focus state
    animations.forEach((anim, index) => {
      const isFocused = state.index === index;
      
      Animated.parallel([
        Animated.spring(anim.scale, {
          toValue: isFocused ? 1.05 : 1,
          useNativeDriver: true,
          tension: 150,
          friction: 12,
        }),
        Animated.timing(anim.opacity, {
          toValue: isFocused ? 1 : 0.6,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });

    // Subtle pulse animation for center button
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05,
          duration: 2000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [state.index]);

  const renderTab = (route: any, index: number) => {
    const { options } = descriptors[route.key];
    const label = options.title !== undefined ? options.title : route.name;
    const isFocused = state.index === index;

    const onPress = () => {
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!isFocused && !event.defaultPrevented) {
        navigation.navigate(route.name);
      }

      // Haptic feedback
      if (Platform.OS === 'ios') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      }
    };

    const { scale, opacity } = animations[index];

    // Use theme colors for active state
    const activeColor = colors.primary; // Theme teal/green
    const inactiveColor = colors.textSecondary;

    return (
      <TouchableOpacity
        key={route.key}
        accessibilityRole="button"
        accessibilityState={isFocused ? { selected: true } : {}}
        accessibilityLabel={options.tabBarAccessibilityLabel}
        testID={options.tabBarTestID}
        onPress={onPress}
        style={styles.tab}
        activeOpacity={0.7}
      >
        <Animated.View
          style={[
            styles.tabContent,
            {
              opacity,
              transform: [{ scale }],
            },
          ]}
        >
          <View style={styles.iconContainer}>
            <Ionicons
              name={getIconName(route.name, isFocused)}
              size={24}
              color={isFocused ? activeColor : inactiveColor}
            />
            {/* Removed notification badge from tab - notifications now centralized in notification center */}
          </View>
          <Text
            style={[
              styles.label,
              {
                color: isFocused ? activeColor : inactiveColor,
              },
            ]}
            numberOfLines={1}
          >
            {label}
          </Text>
        </Animated.View>
      </TouchableOpacity>
    );
  };

  // Don't render the tab bar if it should be hidden
  if (isHidden) {
    return null;
  }

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <BlurView
        intensity={isDark ? 85 : 92}
        tint={isDark ? 'dark' : 'light'}
        style={StyleSheet.absoluteFillObject}
      />
      <View style={styles.tabBar}>
        {/* First two tabs: Dashboard, Purchases */}
        {state.routes.slice(0, 2).map((route, index) => renderTab(route, index))}
        
        {/* Center button */}
        <View style={styles.centerTabContainer}>
          <TouchableOpacity
            style={styles.centerButton}
            onPress={() => {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
              // Navigate to camera capture screen - the primary function of the app
              navigation.navigate('Purchases', {
                screen: 'CapturePurchase',
              });
            }}
            activeOpacity={0.8}
          >
            <Animated.View 
              style={[
                styles.centerButtonInner, 
                { 
                  backgroundColor: colors.primary,
                  transform: [{ scale: pulseAnim }]
                }
              ]}
            >
              <Ionicons name="camera" size={24} color="white" />
            </Animated.View>
          </TouchableOpacity>
          <Text style={[styles.centerLabel, { color: colors.primary }]}>
            Capture
          </Text>
        </View>
        
        {/* Last two tabs: Vendors, Inventory */}
        {state.routes.slice(2).map((route, index) => renderTab(route, index + 2))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',  // Keep absolute for proper tab bar positioning
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
  },
  tabBar: {
    flexDirection: 'row',
    height: 56, // Slightly taller for better center button integration
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
    alignItems: 'flex-end', // Align tabs to bottom for better balance
    paddingBottom: 4,
  },
  tab: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  centerTabContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  centerButton: {
    marginBottom: 4,
  },
  centerButtonInner: {
    width: 48, // Slightly smaller for better balance
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000', // Using black shadow for better universal contrast
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 4,
  },
  centerLabel: {
    fontSize: 10,
    fontWeight: '600',
    marginTop: 2,
  },
  tabContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    marginBottom: 2,
    position: 'relative',
  },
  label: {
    fontSize: 10,
    fontWeight: '500',
    marginTop: 1,
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -10,
    backgroundColor: '#ef4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
  },
  badgeText: {
    color: 'white',
    fontSize: 10,
    fontWeight: '600',
  },
});